import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@/components/atoms/TextField";
import {
  ChargerExcludeErrorType,
  ChargerExcludeCreateModel,
  ChargerExcludeInputModel,
} from "@/models/chargerExclude";
import { Office } from "@/models/office";
import { ChargerNames } from "@/models/chargerNames";
import { ChargerExcludeConstants } from "@/pages/ChargerExclude/ChargerExcludeConstants";
import { Messages } from "@/util/Messages";
import { Picker } from "../atoms/Picker";
import { Autocomplete } from "../molecules/AutoComplete";
import { getJSTCurrentTime } from "@/util/datetime";
import { getOfficeOptionLabel } from "@/util";

export interface AddOrEditChargerProps {
  inputData: ChargerExcludeCreateModel;
  offices?: Office[];
  officeSelected: Office | undefined;
  chargerNames?: ChargerNames[];
  chargerNamesSelected: ChargerNames | undefined;
  errorData: ChargerExcludeErrorType;
  isChargerEdit?: boolean;
  isBeforeToday?: boolean;
  onChangeData: (
    key: keyof ChargerExcludeInputModel,
    value: string | number
  ) => void;
  onChangeChargerNames?: (value: ChargerNames | undefined) => void;
  onChangeOffice?: (value: Office | undefined) => void;
  onChangeDate: (value?: string, inRange?: boolean) => unknown;
}
export const ChargerExcludeCreateEdit = (props: AddOrEditChargerProps) => {
  const {
    inputData,
    errorData,
    isChargerEdit,
    isBeforeToday,
    offices,
    officeSelected,
    chargerNames,
    chargerNamesSelected,
    onChangeData,
    onChangeChargerNames,
    onChangeOffice,
    onChangeDate,
  } = props;

  const minDate = getJSTCurrentTime("datetime");

  return (
    <Table
      size="small"
      sx={{
        borderCollapse: "separate",
        borderSpacing: "0px 4px",
      }}
      className="gx-info-table"
    >
      <TableBody>
        <TableRow sx={{ height: 45 }}>
          <TableCell
            className={isChargerEdit ? "" : "required"}
            sx={isChargerEdit ? { pb: 0 } : undefined}
          >
            {!isChargerEdit ? (
              <Autocomplete
                options={offices || []}
                autoHighlight
                getOptionLabel={getOfficeOptionLabel}
                value={officeSelected ?? undefined}
                onChange={(_, value) => {
                  onChangeOffice && onChangeOffice(value ?? undefined);
                  onChangeData && onChangeData("officeId", value?.id ?? "");
                }}
                label={ChargerExcludeConstants.InstallationLoationOfficeName}
                error={!!errorData.officeId}
                errorLabel={
                  ChargerExcludeConstants.InstallationLoationOfficeName +
                  Messages.PleaseEnter
                }
              />
            ) : (
              <TextField
                label={ChargerExcludeConstants.InstallationLoationOfficeName}
                value={officeSelected?.displayName ?? ""}
                sx={{ width: "100%", wordBreak: "break-all" }}
                multiline
                InputProps={{ readOnly: true, disableUnderline: true }}
              />
            )}
          </TableCell>
        </TableRow>
        <TableRow sx={{ height: 45 }}>
          <TableCell
            className={isChargerEdit ? "" : "required"}
            sx={isChargerEdit ? { pb: 0 } : undefined}
          >
            {!isChargerEdit ? (
              <Autocomplete
                options={chargerNames || []}
                autoHighlight
                getOptionLabel={(opt) => opt?.name ?? ""}
                value={chargerNamesSelected ?? undefined}
                onChange={(_, value) => {
                  onChangeChargerNames &&
                    onChangeChargerNames(value ?? undefined);
                  onChangeData("chargerId", value?.id ?? "");
                }}
                label={ChargerExcludeConstants.ChargerName}
                error={!!errorData.chargerId}
                errorLabel={
                  ChargerExcludeConstants.ChargerName + Messages.PleaseEnter
                }
              />
            ) : (
              <TextField
                label={ChargerExcludeConstants.ChargerName}
                value={chargerNamesSelected?.name}
                InputProps={{ readOnly: true, disableUnderline: true }}
              />
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={isBeforeToday ? "" : "required"}>
            <Picker
              label={ChargerExcludeConstants.SuspensionOfUseStartDate}
              type="datetime-local"
              value={inputData.startAt || ""}
              onChange={(newValue, inRange) => {
                !isBeforeToday && onChangeDate(newValue ?? "", inRange);
              }}
              replaceRangeErrorLabel
              parse={(input) => input}
              decode={(out) => out.slice(0, 16)}
              error={!!errorData.startAt}
              errorLabel={`${ChargerExcludeConstants.SuspensionOfUseStartDate}${Messages.PleaseEnter}`}
              disabled={isBeforeToday}
              minDate={minDate}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="required">
            <TextField
              label={ChargerExcludeConstants.Reason}
              error={!!errorData.cause}
              errorLabel={ChargerExcludeConstants.Reason + Messages.PleaseEnter}
              value={inputData.cause}
              onChange={(e) => {
                onChangeData("cause", e.target.value);
              }}
              inputProps={{ style: { marginLeft: 10 } }}
              sx={{ width: "100%" }}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
